import type {
  MultiListSearchDefinition,
  SearchListExecutionState,
  SearchListFetchResult,
} from './types';

export const createSearchListExecutionState = <
  Item,
  FilterName extends string,
  SortValue extends string,
  Cursor,
>(
  definition: Pick<
    MultiListSearchDefinition<Item, FilterName, SortValue, Cursor>,
    'pageSize' | 'defaultSort'
  >,
  initialFacetState?: SearchListExecutionState<Item, FilterName, SortValue, Cursor>['facetState']
): SearchListExecutionState<Item, FilterName, SortValue, Cursor> => ({
  items: [],
  loading: false,
  hasLoaded: false,
  error: undefined,
  hasMore: false,
  totalCount: undefined,
  take: definition.pageSize,
  sort: definition.defaultSort ?? null,
  nextCursor: undefined,
  facetState: initialFacetState,
});

export const resetSearchListExecutionForCriteriaChange = <
  Item,
  FilterName extends string,
  SortValue extends string,
  Cursor,
>(
  currentState: SearchListExecutionState<Item, FilterName, SortValue, Cursor>,
  definition: Pick<MultiListSearchDefinition<Item, FilterName, SortValue, Cursor>, 'pageSize'>
): SearchListExecutionState<Item, FilterName, SortValue, Cursor> => ({
  ...currentState,
  take: definition.pageSize,
  hasLoaded: false,
  error: undefined,
  nextCursor: undefined,
});

export const resetSearchListExecutionForSortChange = <
  Item,
  FilterName extends string,
  SortValue extends string,
  Cursor,
>(
  currentState: SearchListExecutionState<Item, FilterName, SortValue, Cursor>,
  definition: Pick<MultiListSearchDefinition<Item, FilterName, SortValue, Cursor>, 'pageSize'>,
  sort: SortValue | null
): SearchListExecutionState<Item, FilterName, SortValue, Cursor> => ({
  ...currentState,
  sort,
  take: definition.pageSize,
  hasLoaded: false,
  error: undefined,
  nextCursor: undefined,
});

export const startSearchListReload = <
  Item,
  FilterName extends string,
  SortValue extends string,
  Cursor,
>(
  currentState: SearchListExecutionState<Item, FilterName, SortValue, Cursor>
): SearchListExecutionState<Item, FilterName, SortValue, Cursor> => ({
  ...currentState,
  loading: true,
  error: undefined,
});

export const resolveSearchListReloadSuccess = <
  Item,
  FilterName extends string,
  SortValue extends string,
  Cursor,
>(
  currentState: SearchListExecutionState<Item, FilterName, SortValue, Cursor>,
  definition: Pick<MultiListSearchDefinition<Item, FilterName, SortValue, Cursor>, 'pageSize'>,
  result: SearchListFetchResult<Item, Cursor, FilterName>
): SearchListExecutionState<Item, FilterName, SortValue, Cursor> => {
  const resolvedTake =
    !result.nextCursor && result.totalCount !== undefined
      ? Math.max(definition.pageSize, Math.min(currentState.take, result.totalCount))
      : currentState.take;

  return {
    ...currentState,
    items: result.items,
    loading: false,
    hasLoaded: true,
    error: undefined,
    hasMore: Boolean(result.nextCursor),
    totalCount: result.totalCount,
    take: resolvedTake,
    nextCursor: result.nextCursor,
    // Transition rule: preserve the previous list-level facet state when the result omits it.
    facetState: result.facetState ?? currentState.facetState,
  };
};

export const resolveSearchListReloadError = <
  Item,
  FilterName extends string,
  SortValue extends string,
  Cursor,
>(
  currentState: SearchListExecutionState<Item, FilterName, SortValue, Cursor>,
  error: unknown
): SearchListExecutionState<Item, FilterName, SortValue, Cursor> => ({
  ...currentState,
  loading: false,
  hasLoaded: true,
  error,
});

export const startSearchListLoadMore = <
  Item,
  FilterName extends string,
  SortValue extends string,
  Cursor,
>(
  currentState: SearchListExecutionState<Item, FilterName, SortValue, Cursor>,
  definition: Pick<MultiListSearchDefinition<Item, FilterName, SortValue, Cursor>, 'pageSize'>
): SearchListExecutionState<Item, FilterName, SortValue, Cursor> => ({
  ...currentState,
  loading: true,
  error: undefined,
  take: currentState.take + definition.pageSize,
});

export const resolveSearchListLoadMoreCursor = <
  Item,
  FilterName extends string,
  SortValue extends string,
  Cursor,
>(
  currentState: SearchListExecutionState<Item, FilterName, SortValue, Cursor>,
  definition: Pick<MultiListSearchDefinition<Item, FilterName, SortValue, Cursor>, 'pageSize'>
): Cursor | undefined => {
  if (
    typeof currentState.nextCursor === 'number' &&
    currentState.take > definition.pageSize &&
    currentState.take % definition.pageSize === 0
  ) {
    return (currentState.take / definition.pageSize + 1) as Cursor;
  }

  return currentState.nextCursor;
};

export const resolveSearchListLoadMoreSuccess = <
  Item,
  FilterName extends string,
  SortValue extends string,
  Cursor,
>(
  currentState: SearchListExecutionState<Item, FilterName, SortValue, Cursor>,
  definition: Pick<MultiListSearchDefinition<Item, FilterName, SortValue, Cursor>, 'pageSize'>,
  result: SearchListFetchResult<Item, Cursor, FilterName>
): SearchListExecutionState<Item, FilterName, SortValue, Cursor> => {
  const resolvedTake =
    !result.nextCursor && result.totalCount !== undefined
      ? Math.max(definition.pageSize, Math.min(currentState.take, result.totalCount))
      : currentState.take;

  return {
    ...currentState,
    items: [...currentState.items, ...result.items],
    loading: false,
    error: undefined,
    hasMore: Boolean(result.nextCursor),
    totalCount: result.totalCount ?? currentState.totalCount,
    take: resolvedTake,
    nextCursor: result.nextCursor,
    // Transition rule: preserve the previous list-level facet state when the result omits it.
    facetState: result.facetState ?? currentState.facetState,
  };
};

export const resolveSearchListLoadMoreError = <
  Item,
  FilterName extends string,
  SortValue extends string,
  Cursor,
>(
  currentState: SearchListExecutionState<Item, FilterName, SortValue, Cursor>,
  definition: Pick<MultiListSearchDefinition<Item, FilterName, SortValue, Cursor>, 'pageSize'>,
  error: unknown
): SearchListExecutionState<Item, FilterName, SortValue, Cursor> => ({
  ...currentState,
  loading: false,
  error,
  take: Math.max(definition.pageSize, currentState.take - definition.pageSize),
});

export const canSearchListLoadMore = <
  Item,
  FilterName extends string,
  SortValue extends string,
  Cursor,
>(
  state: SearchListExecutionState<Item, FilterName, SortValue, Cursor>
) => !state.loading && state.hasMore && state.nextCursor !== undefined && state.nextCursor !== null;
